import { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './index.module.scss';
import SiteTopNav from '@/components/SiteTopNav';

const ChevronIcon = props => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false" {...props}>
    <path
      d="M6 9l6 6 6-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: '1.5',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
  focusable: 'false',
};

const MopIcon = () => (
  <svg {...iconProps}>
    <path d="M12 3v9" />
    <path d="M8 21l1.5-9h5L16 21" />
    <path d="M7 21h10" />
  </svg>
);

const RefreshIcon = () => (
  <svg {...iconProps}>
    <path d="M4 12a8 8 0 0 1 13.66-5.66" />
    <path d="M20 4v5h-5" />
    <path d="M20 12a8 8 0 0 1-13.66 5.66" />
    <path d="M4 20v-5h5" />
  </svg>
);

const DropletIcon = () => (
  <svg {...iconProps}>
    <path d="M12 3s7 7.5 7 12a7 7 0 1 1-14 0c0-4.5 7-12 7-12z" />
  </svg>
);

const ShieldIcon = () => (
  <svg {...iconProps}>
    <path d="M12 3l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V6l7-3z" />
  </svg>
);

const AvoidIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9 9l6 6M15 9l-6 6" />
  </svg>
);

const CheckIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12.5l2.5 2.5L16 9.5" />
  </svg>
);

const quickCareItems = [
  {
    icon: MopIcon,
    title: 'Daily Cleaning',
    text: 'A quick dust-mop pass lifts grit before it can scratch the surface underfoot.',
  },
  {
    icon: RefreshIcon,
    title: 'Weekly Cleaning',
    text: 'A soft-brush vacuum pass clears dust from seams, corners, and edges.',
  },
  {
    icon: DropletIcon,
    title: 'Spill Cleanup',
    text: 'Wipe spills right away so moisture never has a chance to reach the wood.',
  },
  {
    icon: ShieldIcon,
    title: 'Long-Term Protection',
    text: 'Felt pads, rugs, and steady humidity keep floors beautiful for years to come.',
  },
];

const avoidItems = [
  {
    title: 'Steam Mops',
    text: 'Heat and moisture can soften the finish and cause planks to warp over time.',
  },
  {
    title: 'Excess Water',
    text: 'Standing water can seep between boards and lead to swelling or cupping.',
  },
  {
    title: 'Wax',
    text: 'Wax builds up in layers, dulling the finish and complicating future refinishing.',
  },
  {
    title: 'Oil Soap',
    text: 'Leaves a residue that clouds the finish and attracts more dirt over time.',
  },
  {
    title: 'Abrasive Pads',
    text: 'Scouring pads and stiff-bristle brushes can scratch through the wear layer.',
  },
  {
    title: 'Harsh Chemicals',
    text: 'Vinegar and ammonia-based cleaners can dull, etch, or discolor the finish.',
  },
];

const protectItems = [
  { title: 'Felt Pads', text: 'Add felt pads under furniture legs to prevent scuffs and dents.' },
  {
    title: 'Entry Rugs',
    text: 'Place rugs at entrances to catch dirt and grit before it reaches the floor.',
  },
  {
    title: 'Maintain Humidity',
    text: 'Keep indoor humidity between roughly 35–55% to prevent gaps and cupping.',
  },
  {
    title: 'Trim Pet Nails',
    text: 'Regular trims help prevent everyday surface scratches from claws.',
  },
  {
    title: 'Lift, Don’t Drag',
    text: 'Lift furniture when moving it rather than dragging it across the floor.',
  },
  {
    title: 'Wipe Spills Immediately',
    text: 'Address spills right away to prevent staining or moisture damage.',
  },
];

const faqItems = [
  {
    question: 'Can I use a steam mop?',
    answer:
      'No — steam mops introduce heat and moisture that can soften the finish and, over time, warp the planks. A lightly damp microfiber mop is the safer choice.',
  },
  {
    question: 'Can I use vinegar?',
    answer:
      'We don’t recommend it. Vinegar is acidic and repeated use can dull or etch a hardwood finish. Choose a pH-neutral cleaner made specifically for hardwood instead.',
  },
  {
    question: 'What cleaner do you recommend?',
    answer:
      'Use a cleaner formulated specifically for hardwood floors, applied sparingly with a microfiber mop. Avoid all-purpose or multi-surface cleaning products.',
  },
  {
    question: 'How often should I clean?',
    answer:
      'Dust mop or vacuum a few times a week in busier homes, and follow with a deeper clean using a hardwood cleaner every one to two weeks.',
  },
  {
    question: 'Can engineered hardwood get wet?',
    answer:
      'Engineered hardwood is more dimensionally stable than solid hardwood, but it isn’t waterproof. Wipe up spills promptly and avoid letting water stand on the surface.',
  },
];

const FaqAccordion = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className={styles['faq-list']}>
      {faqItems.map((item, index) => {
        const isOpen = openIndex === index;
        const panelId = `floor-care-faq-panel-${index}`;
        return (
          <div key={item.question} className={styles['faq-item']}>
            <h3 className={styles['faq-item__heading']}>
              <button
                type="button"
                className={styles['faq-item__trigger']}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenIndex(isOpen ? null : index)}
              >
                {item.question}
                <ChevronIcon
                  className={[styles['faq-item__chevron'], isOpen ? styles['is-open'] : '']
                    .filter(Boolean)
                    .join(' ')}
                />
              </button>
            </h3>
            <div
              id={panelId}
              className={[styles['faq-item__panel'], isOpen ? styles['is-open'] : '']
                .filter(Boolean)
                .join(' ')}
            >
              <p className={styles['faq-item__answer']}>{item.answer}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const FloorCare = () => {
  return (
    <div className={styles['floor-care-page']}>
      <div className={styles.page}>
        <section className={styles['guide-hero']}>
          <div className={styles['guide-hero__overlay']}>
            <SiteTopNav />
            <div className={styles['guide-hero__content']}>
              <h1 className={styles['guide-hero__title']} data-motion="hero">
                Floor Care &amp; Maintenance
              </h1>
              <p className={styles['guide-hero__subtitle']} data-motion="copy">
                Simple care for lasting beauty.
              </p>
            </div>
          </div>
        </section>

        <main className={styles['guide-main']}>
          <section className={styles['care-cards']} aria-label="Quick care overview">
            {quickCareItems.map(item => (
              <article key={item.title} className={styles['care-card']} data-motion="card">
                <span className={styles['care-card__icon']}>
                  <item.icon />
                </span>
                <h2 className={styles['care-card__title']}>{item.title}</h2>
                <p className={styles['care-card__text']}>{item.text}</p>
              </article>
            ))}
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Everyday Care</p>
            <h2 className={styles['article-section__title']}>Daily Cleaning</h2>
            <p className={styles['article-section__text']}>
              A little daily attention is all it takes to keep hardwood looking its best.
            </p>
            <ul className={styles['article-list']}>
              <li>Use a microfiber dust mop to lift dust without scattering it</li>
              <li>A soft-brush vacuum attachment works well for a deeper daily pass</li>
              <li>Focus on entryways and high-traffic paths where dirt and grit collect</li>
              <li>Avoid vacuum heads with a beater bar — they can scratch the finish</li>
            </ul>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Periodic Care</p>
            <h2 className={styles['article-section__title']}>Deep Cleaning</h2>
            <p className={styles['article-section__text']}>
              Every one to two weeks, a deeper clean keeps the finish looking refined.
            </p>
            <ul className={styles['article-list']}>
              <li>Use a damp — never wet — microfiber mop for a deeper clean</li>
              <li>Choose a cleaner made specifically for hardwood floors</li>
              <li>Never pour cleaner directly onto the floor; apply it to the mop instead</li>
              <li>Clean with the grain of the wood for the most even result</li>
              <li>Dry the surface immediately after cleaning</li>
            </ul>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Cleaning Product</p>
            <h2 className={styles['article-section__title']}>
              Choosing the Right Hardwood Floor Cleaner
            </h2>
            <p className={styles['article-section__text']}>
              It’s worth using a cleaner made specifically for hardwood floors rather than a
              general-purpose product. Specialized formulas are non-abrasive, leave no residue
              behind, and dry quickly with no rinsing required — most also emit no VOCs, which is
              better for your family, pets, and the air inside your home.
            </p>
            <p className={styles['article-section__text']}>
              To clean and maintain your Sakura Floors hardwood, we recommend{' '}
              <a
                href="https://www.bona.com/en-us/products/homeowners/cleaners/wood-floor/hw-clnr-spray-32oz/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.cleanerBadge}
              >
                Bona Hardwood Floor Cleaner
              </a>
            </p>
          </section>

          <section className={styles['avoid-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Handle With Care</p>
            <h2 className={styles['article-section__title']}>Things to Avoid</h2>
            <div className={styles['avoid-grid']}>
              {avoidItems.map(item => (
                <article key={item.title} className={styles['avoid-card']} data-motion="card">
                  <span className={styles['avoid-card__icon']}>
                    <AvoidIcon />
                  </span>
                  <h3 className={styles['avoid-card__title']}>{item.title}</h3>
                  <p className={styles['avoid-card__text']}>{item.text}</p>
                </article>
              ))}
            </div>
          </section>

          <section className={styles['protect-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Everyday Habits</p>
            <h2 className={styles['article-section__title']}>Protect Your Investment</h2>
            <div className={styles['protect-grid']}>
              {protectItems.map(item => (
                <article key={item.title} className={styles['protect-card']} data-motion="card">
                  <span className={styles['protect-card__icon']}>
                    <CheckIcon />
                  </span>
                  <h3 className={styles['protect-card__title']}>{item.title}</h3>
                  <p className={styles['protect-card__text']}>{item.text}</p>
                </article>
              ))}
            </div>
          </section>

          <section className={styles['faq-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Common Questions</p>
            <h2 className={styles['article-section__title']}>Frequently Asked Questions</h2>
            <FaqAccordion />
          </section>

          <section className={styles['cta-section']} data-motion="section">
            <h2 className={styles['cta-section__title']}>Need help choosing the right cleaner?</h2>
            <Link className={styles['cta-section__button']} to="/contact-us">
              <span>Contact Sakura Floors</span>
            </Link>
          </section>
        </main>
      </div>
    </div>
  );
};

export default FloorCare;
